import * as React from 'react';

import { enumLoadWeightType } from '@rematter/modules/trade/types/load';
import { cn } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $deleteEntry,
  $itemsInterfaceDisabled,
  $itemsInterfaceFacilityDefaultUnit,
  $itemsInterfaceInventoryLocked,
  $itemsInterfacePricingLocked,
  $itemsInterfaceShowPricing,
  $itemsInterfaceWeightUnitMode,
  $updateLineItem,
} from '../_state';
import { NormalizedLineItem } from '../_types';
import {
  CameraButtonField,
  DeleteButtonField,
  EditItemButtonField,
  EstimatedPriceField,
  GrossWeightField,
  InventoryItemField,
  NetWeightField,
  PricingUnitField,
  TareWeightField,
  UnitPriceField,
} from '../fields';
import {
  getItemRowGridCols,
  ITEM_ROW_ERROR_BORDER,
  ITEM_ROW_FOCUS_WITHIN,
} from '../layout';
import { ItemRow } from '../layout/ItemRow';

import {
  $focusedField,
  $remoteFocusPositions,
} from '~/modules/trade/_state/fieldPresence.state';
import { $openRawMaterialDetails } from '~/modules/trade/_state/materialsInterface.state';
import { FieldFocusIndicator } from '~/shared/components/FieldFocusIndicator';
import { useUIPreferences } from '~/shared/hooks';

type ItemRowMaterialProps = {
  lineItem: NormalizedLineItem;
  autoFocus?: boolean;
  lineItemClassName?: string;
  className?: string;
  onFocusRow?: (id: string) => void;
  hasError?: boolean;
  onAddNextRow?: () => void;
};

//
// ⚛️ ⚛️ ⚛️ ITEM ROW MATERIAL ⚛️ ⚛️ ⚛️
//
export const ItemRowMaterial: React.FC<ItemRowMaterialProps> = ({
  lineItem,
  className,
  lineItemClassName,
  autoFocus = false,
  onFocusRow,
  hasError,
  onAddNextRow,
}) => {
  // Refs
  const inventorySelectRef = React.useRef<HTMLDivElement>(null);
  const grossWeightRef = React.useRef<HTMLDivElement>(null);
  const tareWeightRef = React.useRef<HTMLDivElement>(null);
  const netWeightRef = React.useRef<HTMLDivElement>(null);
  const unitPriceRef = React.useRef<HTMLDivElement>(null);
  const pricingUnitRef = React.useRef<HTMLDivElement>(null);

  const setFocusedField = useSetAtom($focusedField);
  const focusPositions = useAtomValue($remoteFocusPositions);

  // Atoms
  const disabled = useAtomValue($itemsInterfaceDisabled);
  const inventoryLocked = useAtomValue($itemsInterfaceInventoryLocked);
  const pricingLocked = useAtomValue($itemsInterfacePricingLocked);
  const showPricing = useAtomValue($itemsInterfaceShowPricing);
  const weightUnit = useAtomValue($itemsInterfaceWeightUnitMode);
  const facilityDefaultUnit = useAtomValue($itemsInterfaceFacilityDefaultUnit);
  const onUpdateItem = useSetAtom($updateLineItem);
  const onDeleteItem = useSetAtom($deleteEntry);
  const openRawMaterialDetails = useSetAtom($openRawMaterialDetails);

  const { touchModeEnabled } = useUIPreferences();

  const inventoryFieldsDisabled = disabled || inventoryLocked;
  const pricingFieldsDisabled = disabled || pricingLocked;

  const baseFieldProps = {
    lineItem,
    onUpdateItem,
    onDeleteItem,
    weightUnit,
    facilityDefaultUnit,
  };

  const inventoryFieldProps = {
    ...baseFieldProps,
    disabled: inventoryFieldsDisabled,
  };

  const pricingFieldProps = {
    ...baseFieldProps,
    disabled: pricingFieldsDisabled,
  };

  const gridClassName = getItemRowGridCols(showPricing);

  // Callbacks
  const focusRef = React.useCallback(
    (ref: React.RefObject<HTMLDivElement | null>) => {
      ref.current
        ?.querySelector<HTMLElement>('input, button, [tabindex]')
        ?.focus();
    },
    [],
  );

  const handleAfterMaterialSelect = React.useCallback(() => {
    focusRef(grossWeightRef);
  }, [focusRef]);

  const handleInventoryKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      focusRef(grossWeightRef);
    },
    [focusRef],
  );

  const handleGrossWeightKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      focusRef(tareWeightRef);
    },
    [focusRef],
  );

  const handleTareWeightKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      focusRef(netWeightRef);
    },
    [focusRef],
  );

  const handleNetWeightKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      if (showPricing) {
        focusRef(unitPriceRef);
      } else if (!disabled && onAddNextRow) {
        onAddNextRow();
      }
    },
    [showPricing, disabled, onAddNextRow, focusRef],
  );

  const handleUnitPriceKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      focusRef(pricingUnitRef);
    },
    [focusRef],
  );

  const handlePricingUnitKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      if (!disabled && onAddNextRow) {
        onAddNextRow();
      }
    },
    [disabled, onAddNextRow],
  );

  const handleOpenDetails = React.useCallback(() => {
    openRawMaterialDetails({
      lineItemId: lineItem.id,
      grossWeight: lineItem.weights?.[enumLoadWeightType.Gross]?.value ?? 0,
      tareWeight: lineItem.weights?.[enumLoadWeightType.Tare]?.value ?? 0,
      notes: lineItem.notes ?? '',
    });
  }, [lineItem.id, lineItem.weights, lineItem.notes, openRawMaterialDetails]);

  // Effects
  React.useEffect(() => {
    if (!autoFocus) return;

    const isNewItem = lineItem.id.startsWith('temp-');
    const targetRef =
      touchModeEnabled || !isNewItem ? grossWeightRef : inventorySelectRef;

    const inputElement = targetRef?.current?.querySelector('input');
    if (inputElement) {
      setTimeout(() => {
        inputElement.focus();
        inputElement.select();
      }, 100);
    }
  }, [autoFocus, touchModeEnabled]);

  return (
    <FieldFocusIndicator
      fieldId={`lineItem:${lineItem.id}`}
      focusPositions={focusPositions}
      className="@ItemRowMaterial"
    >
      <div
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setFocusedField(null);
          }
        }}
      >
        <ItemRow
          gridTemplate={gridClassName}
          className={cn(
            '@ItemRowMaterial relative px-1',
            ITEM_ROW_FOCUS_WITHIN,
            hasError && ITEM_ROW_ERROR_BORDER,
            className,
          )}
          onFocus={() => {
            onFocusRow?.(lineItem.id);
            setFocusedField(`lineItem:${lineItem.id}`);
          }}
        >
          <div ref={inventorySelectRef} className={lineItemClassName}>
            <InventoryItemField
              {...inventoryFieldProps}
              lineItem={lineItem}
              onKeyDown={handleInventoryKeyDown}
              onAfterSelect={handleAfterMaterialSelect}
            />
          </div>

          <div ref={grossWeightRef}>
            <GrossWeightField
              {...inventoryFieldProps}
              onKeyDown={handleGrossWeightKeyDown}
            />
          </div>

          <div ref={tareWeightRef}>
            <TareWeightField
              {...inventoryFieldProps}
              onKeyDown={handleTareWeightKeyDown}
            />
          </div>

          <div ref={netWeightRef}>
            <NetWeightField
              {...inventoryFieldProps}
              onKeyDown={handleNetWeightKeyDown}
            />
          </div>

          {showPricing && (
            <>
              <div ref={unitPriceRef}>
                <UnitPriceField
                  {...pricingFieldProps}
                  onKeyDown={handleUnitPriceKeyDown}
                />
              </div>
              <div ref={pricingUnitRef}>
                <PricingUnitField
                  {...pricingFieldProps}
                  onKeyDown={handlePricingUnitKeyDown}
                />
              </div>
              <EstimatedPriceField {...pricingFieldProps} />
            </>
          )}

          <EditItemButtonField
            {...inventoryFieldProps}
            onClick={handleOpenDetails}
          />
          <CameraButtonField {...inventoryFieldProps} disabled />
          <DeleteButtonField {...inventoryFieldProps} />
        </ItemRow>
      </div>
    </FieldFocusIndicator>
  );
};
